import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import Swal from "sweetalert2";

import MapasCard from "./MapasCard";
import { useRelacionTurnos, SelectorEsquema, GrupoTurnos, horaEstructura } from "./RelacionTurnos";
import { formatearHoraAmPm } from "../../../../utilidades/formateos";
import DialogCostos from "../../../../components/molecules/DialogCostos/DialogCostos";
import { actualizarCostosRuta, requestChanges } from "../Integracion";
import usePermisosModulo from "../../../../utilidades/customHooks/usePermisosModulo";

/**
 * Formatea un valor monetario a pesos mexicanos.
 */
const formatearPrecio = (valor) => {
    if (valor == null) return "—";
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(valor);
};

/**
 * Chip visual para mostrar si un equipamiento está incluido.
 */
const ChipEquipamiento = ({ activo, label, icon }) => (
    <div className={`ruta-card__equip-chip ${activo ? "ruta-card__equip-chip--activo" : "ruta-card__equip-chip--inactivo"}`}>
        <i className={icon} />
        <span>{label}</span>
    </div>
);

/**
 * Componente de días de la semana con indicador visual.
 */
const DiasOperacion = ({ estructura, diasSemana }) => (
    <div className="ruta-card__dias">
        {diasSemana.map((dia) => (
            <span key={dia.campo} className={`ruta-card__dia ${estructura[dia.campo] ? "ruta-card__dia--activo" : ""}`} title={dia.label}>
                {dia.label}
            </span>
        ))}
    </div>
);

/**
 * Un dato de costo (label + valor + comparación con valor anterior si cambió).
 */
const CostoItem = ({ label, campo, resaltado, ruta, rutaAnterior, sugerenciaCampo }) => {
    const anterior = rutaAnterior?.[campo];
    const actual = ruta[campo];
    const cambio = anterior !== undefined && anterior !== actual;
    const sugerencia = sugerenciaCampo ? ruta?.[sugerenciaCampo] : null;

    return (
        <div className="ruta-card__costo-item">
            <span className="ruta-card__dato-label">{label}</span>
            <span className={`ruta-card__dato-valor${resaltado ? " ruta-card__dato-valor--resaltado" : ""}`}>
                {formatearPrecio(actual)}
            </span>
            {cambio && (
                <span className="ruta-card__dato-anterior">
                    <i className="pi pi-arrow-right mr-1" />
                    antes: {formatearPrecio(anterior)}
                </span>
            )}
            {sugerencia != null && (
                <span className="ruta-card__dato-sugerencia">
                    <i className="pi pi-info-circle mr-1" />
                    El pago al operador debería ser {formatearPrecio(sugerencia)}
                </span>
            )}
        </div>
    );
};

/**
 * El precio al cliente es el dato que la dueña consulta a diario: se muestra
 * en un bloque propio y grande. Los demás pagos quedan discretos, no ocultos.
 */
const PrecioClienteDestacado = ({ ruta, rutaAnterior }) => {
    const anterior = rutaAnterior?.precioCliente;
    const actual = ruta.precioCliente;
    const cambio = anterior !== undefined && anterior !== actual;

    return (
        <div className="ruta-card__precio-destacado">
            <span className="ruta-card__dato-label">Precio Cliente</span>
            <span className="ruta-card__precio-destacado-valor">{formatearPrecio(actual)}</span>
            {cambio && (
                <span className="ruta-card__dato-anterior">
                    <i className="pi pi-arrow-right mr-1" />
                    antes: {formatearPrecio(anterior)}
                </span>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════
   SECCIÓN: Costos
   ═══════════════════════════════════════════ */
const SeccionCostos = ({ visibilidad, ruta, rutaAnterior, estatus, puedeDefinirCostos, puedeEnviarCliente, userId, onDefinirCostos, onSendToClient, onSolicitarCambios }) => {
    if (!visibilidad.costos || (!puedeDefinirCostos && !puedeEnviarCliente)) return null;

    const camposVisibles = {
        pagoChofer: userId !== 2,
        pagoChoferSencillo: userId !== 2,
        precioCliente: true,
        pagoSubcontratista: true,
    };

    const puedeEnviar = visibilidad.puedeEditarCostos && puedeEnviarCliente && (estatus === 3 || estatus === 9);

    return (
        <div className="ruta-card__costos-seccion">
            <span className="ruta-card__seccion-mini-label">
                <i className="pi pi-dollar" /> Costos
            </span>

            {puedeDefinirCostos && (
                <div className="ruta-card__costos-layout">
                    {visibilidad.precioCliente && camposVisibles.precioCliente && (
                        <PrecioClienteDestacado ruta={ruta} rutaAnterior={rutaAnterior} />
                    )}

                    <div className="ruta-card__costos-secundarios">
                        {visibilidad.pagoChofer && camposVisibles.pagoChofer && (
                            <CostoItem label="Pago Chofer" campo="pagoChofer" ruta={ruta} rutaAnterior={rutaAnterior} sugerenciaCampo="pagoCorrespondiente" />
                        )}
                        {visibilidad.pagoChofer && camposVisibles.pagoChoferSencillo && (
                            <CostoItem label="Pago Chofer Sencillo" campo="pagoChoferSencillo" ruta={ruta} rutaAnterior={rutaAnterior} sugerenciaCampo="pagoCorrespondienteS" />
                        )}
                        {visibilidad.pagoSubcontratista && camposVisibles.pagoSubcontratista && (
                            <CostoItem label="Pago Subcontratista" campo="pagoSubcontratista" ruta={ruta} rutaAnterior={rutaAnterior} />
                        )}
                    </div>
                </div>
            )}

            {(puedeDefinirCostos && visibilidad.puedeEditarCostos) || puedeEnviarCliente ? (
                <div className="ruta-card__costo-acciones flex flex-wrap gap-2">
                    {puedeDefinirCostos && visibilidad.puedeEditarCostos && (
                        <>
                            {!puedeEnviar && (
                                <Button label="Definir costos" icon="pi pi-dollar" className="p-button-sm" onClick={onDefinirCostos} />
                            )}

                            {puedeEnviar && (
                                <>
                                    <Button label="Definir costos" icon="pi pi-dollar" severity="secondary" outlined className="p-button-sm" onClick={onDefinirCostos} />
                                    <Button label="Solicitar Cambios" icon="pi pi-refresh" severity="danger" outlined className="p-button-sm" onClick={onSolicitarCambios} />
                                </>
                            )}
                        </>
                    )}

                    {puedeEnviarCliente && (
                        <Button
                            label="Enviar al cliente"
                            icon="pi pi-send"
                            className="p-button-sm"
                            onClick={onSendToClient}
                            disabled={!ruta.precioCliente || estatus !== 9}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
};

/* ═══════════════════════════════════════════
   SECCIÓN: Equipamiento
   ═══════════════════════════════════════════ */
const SeccionEquipamiento = ({ visibilidad, ruta, equipamiento, historial, puedeConfirmarDatos, verificado, onValidarEstructuras }) => {
    if (!visibilidad.equipamiento) return null;

    const mostrarBotonConfirmar =
        Array.isArray(ruta.estructuras) &&
        ruta.estructuras.length > 0 &&
        !historial?.some((h) => h.cambio === 2) &&
        !verificado &&
        puedeConfirmarDatos;

    return (
        <div className="ruta-card__equip-fila">
            <span className="ruta-card__equip-label">
                <i className="pi pi-wrench" /> Equipamiento
            </span>
            {equipamiento.map((eq) => (
                <ChipEquipamiento key={eq.campo} activo={eq.activo} label={eq.label} icon={eq.icon} />
            ))}
            <ChipEquipamiento activo={ruta.combinable} label="Combinable" icon="pi pi-link" />
            {mostrarBotonConfirmar && (
                <Button
                    label="Confirmar datos y continuar"
                    icon="pi pi-check"
                    className="ml-auto p-button-sm"
                    severity="warning"
                    tooltip="Bloquea los datos actuales y avanza al siguiente paso"
                    tooltipOptions={{ position: "top" }}
                    onClick={onValidarEstructuras}
                />
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════
   SECCIÓN: Cambios de ruta (solo modificación)
   ═══════════════════════════════════════════ */
const SeccionCambios = ({ esModificacion, historial, cambiosRuta }) => {
    if (!esModificacion || (!historial?.length && !cambiosRuta?.length)) return null;

    return (
        <div className="ruta-card__cambios-bloque">
            <span className="ruta-card__seccion-mini-label">
                <i className="pi pi-history" /> Cambios realizados
            </span>
            <div className="ruta-card__cambios-lista">
                {historial?.map((h) => (
                    <div key={h.id} className="ruta-card__cambio-check">
                        <i className="pi pi-check" /> {h.nombreCambio}
                    </div>
                ))}
                {cambiosRuta?.map((c, i) => (
                    <div key={i} className="ruta-card__cambio-check">
                        <i className="pi pi-check" />
                        <span className="ruta-card__cambio-campo">{c.campo}</span>
                        <span className="ruta-card__cambio-anterior">
                            {c.anterior}
                            <i className="pi pi-arrow-right" style={{ fontSize: "0.7rem" }} />
                        </span>
                        <span className="ruta-card__cambio-actual">{c.actual}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════
   SECCIÓN: Horarios y estructuras
   ═══════════════════════════════════════════ */

/**
 * Fila de un horario (estructura). `sinCambios` la muestra como estructura de producción que se conserva.
 */
const FilaHorario = ({ estructura, ruta, diasSemana, visibilidad, sinCambios = false, cambios = [], nota }) => (
    <div className={`ruta-card__horario-fila ${sinCambios ? "ruta-card__horario-fila--sin-cambios" : ""}`}>
        <span className="ruta-card__horario-tiempo">
            <i className="pi pi-clock" />
            {formatearHoraAmPm(horaEstructura(estructura))}
        </span>

        <DiasOperacion estructura={estructura} diasSemana={diasSemana} />

        <span className="ruta-card__horario-tag ruta-card__horario-tag--fija relacion-turnos__tag-turno">Turno {estructura.nombreTurno}</span>
        <span className="ruta-card__horario-tag ruta-card__horario-tag--fija">{estructura.nombreTipo}</span>

        {/* Explica por qué una salida flexible (SSD) quedó en este viaje */}
        {nota && (
            <span className="relacion-turnos__nota-fila">
                <i className="pi pi-info-circle" /> {nota}
            </span>
        )}

        <div className="ruta-card__horario-badges">
            {sinCambios
                ? <span className="ruta-card__horario-tag ruta-card__horario-tag--sin-cambios">Sin cambios</span>
                : <span className="ruta-card__horario-tag ruta-card__horario-tag--fija">{ruta.fija ? "Fija" : "Eventual"}</span>}
        </div>

        {cambios.length > 0 && (
            <div className="ruta-card__cambios-estructura">
                {cambios.map((c, i) => (
                    <span key={i} className="ruta-card__cambio-chip">
                        <span className="ruta-card__cambio-chip-campo">{c.campo}:</span>
                        <span className="ruta-card__cambio-chip-antes">{String(c.anterior)}</span>
                        <i className="pi pi-arrow-right" style={{ fontSize: "0.6rem" }} />
                        <span className="ruta-card__cambio-chip-despues">{String(c.actual)}</span>
                    </span>
                ))}
            </div>
        )}

        {visibilidad.kilometraje && (
            <span className="ruta-card__horario-km">{estructura.kilometros ? estructura.kilometros : "--.--"} km</span>
        )}
    </div>
);

const SeccionHorarios = ({ visibilidad, ruta, estructurasOrdenadas, estructurasSinCambios = [], diasSemana, esModificacion, getCambiosEstructura }) => {
    // El esquema se calcula con todas las estructuras que tendrá la ruta (incluye las que se conservan)
    const todasLasEstructuras = useMemo(
        () => [...estructurasOrdenadas, ...(esModificacion ? estructurasSinCambios : [])],
        [estructurasOrdenadas, estructurasSinCambios, esModificacion]
    );
    const relacion = useRelacionTurnos(todasLasEstructuras);

    if (!visibilidad.estructuras || !ruta.estructuras?.length) return null;

    const idsSinCambios = new Set(esModificacion ? estructurasSinCambios.map((e) => e.id) : []);

    return (
        <div className="ruta-card__horarios-seccion">
            <div className="relacion-turnos__encabezado">
                <span className="ruta-card__seccion-mini-label m-0">
                    <i className="pi pi-calendar" /> Horarios y Estructuras
                </span>
                <SelectorEsquema relacion={relacion} />
            </div>

            {/* Las entradas y salidas del mismo viaje se agrupan y se unen con una línea de color */}
            <div className="ruta-card__horarios-lista">
                {relacion.agrupar(todasLasEstructuras).map(({ momento, estructuras, extras, notas }) => (
                    <GrupoTurnos key={momento?.clave ?? "fuera"} momento={momento} extras={extras}>
                        {estructuras.map((estructura) => (
                            <FilaHorario
                                key={estructura.id}
                                estructura={estructura}
                                ruta={ruta}
                                diasSemana={diasSemana}
                                visibilidad={visibilidad}
                                nota={notas?.[estructura.id]}
                                sinCambios={idsSinCambios.has(estructura.id)}
                                cambios={esModificacion ? getCambiosEstructura(estructura.id) : []}
                            />
                        ))}
                    </GrupoTurnos>
                ))}
            </div>
        </div>
    );
};


/* ═══════════════════════════════════════════
   Tarjeta principal de la ruta
   ═══════════════════════════════════════════ */
const TarjetaRuta = ({
                         cotizacion,
                         diasSemana,
                         visibilidad,
                         onGenerarTrazo,
                         handleEditarAllTrazos,
                         onEditarTrazo,
                         onValidarEstructuras,
                         onValidarTrazo,
                         setCotizacion,
                         onSendToClient,
                         permisosFlags = {},
                         onSolicitarCambios,
                     }) => {
    const {
        id: cotizacionId,
        rutas: ruta,
        rutaAnterior,
        idCategoria: categoria,
        estatus,
        cambiosRuta = [],
        historial = [],
        cambiosEstructuras = [],
        estructurasSinCambios = [],
        observaciones,
        cambiosSolicitados,
    } = cotizacion;

    const { userId } = usePermisosModulo("Cotizaciones empresariales", "Listado de cotizaciones");

    const equipamiento = useMemo(
        () => [
            { campo: "autDireccion", label: "Dir. Asistida", icon: "pi pi-car", activo: ruta.autDireccion },
            { campo: "ieAire", label: "Aire Acond.", icon: "pi pi-sun", activo: ruta.ieAire },
            { campo: "ieAforo", label: "Aforo", icon: "pi pi-users", activo: ruta.ieAforo },
            { campo: "ieCamaraReversa", label: "Cám. Reversa", icon: "pi pi-video", activo: ruta.ieCamaraReversa },
            { campo: "ieFatiga", label: "Sensor Fatiga", icon: "pi pi-eye", activo: ruta.ieFatiga },
        ],
        [ruta]
    );

    const { puedeConfirmarDatos = false, puedeVerificarTrazos = false, puedeDefinirCostos = false, puedeEnviarCliente = false } = permisosFlags;

    const ORDEN_TURNOS = Object.fromEntries(
        ["E1", "S3", "E2", "S1", "E3", "S2"].map((turno, index) => [turno, index])
    );

    const compararEstructuras = (a, b) => {
        const ordenA = ORDEN_TURNOS[a.nombreTurno] ?? 999;
        const ordenB = ORDEN_TURNOS[b.nombreTurno] ?? 999;
        if (ordenA !== ordenB) return ordenA - ordenB;
        return (a.horaSalida ?? '').localeCompare(b.horaSalida ?? '');
    };

    const estructurasOrdenadas = [...(ruta.estructuras ?? [])].sort(compararEstructuras);
    const estructurasSinCambiosOrdenadas = [...estructurasSinCambios].sort(compararEstructuras);

    const esModificacion = categoria === 5;
    const esNuevaRuta = categoria === 3;
    const datosHeader = categoria === 3 ? ruta : rutaAnterior;

    // Solo estos tipos de cambio afectan el recorrido/trazo. Si una modificación
    // es, por ejemplo, únicamente "Cambio de unidad", no debe mostrarse la
    // generación de trazos porque el recorrido no cambia.
    const TIPOS_CAMBIO_QUE_AFECTAN_TRAZO = [3, 4, 5]; // Extensión, Reducción, Modificar paradas
    const requiereGestionTrazos =
        !esModificacion || historial?.some((h) => TIPOS_CAMBIO_QUE_AFECTAN_TRAZO.includes(h.cambio));

    // Si es ruta nueva, primero deben confirmar los datos de equipamiento/horarios
    // antes de poder generar los recorridos (trazos).
    const verificado = ruta.estructuras?.some((est) => est.verificado) ?? false;
    const puedeGenerarTrazos = !esNuevaRuta || verificado;

    const [dialogCostosVisible, setDialogCostosVisible] = useState(false);
    const [guardandoCostos, setGuardandoCostos] = useState(false);
    const [dialogCambiosVisible, setDialogCambiosVisible] = useState(false);
    const [motivoCambios, setMotivoCambios] = useState("");
    const [enviandoCambios, setEnviandoCambios] = useState(false);

    const getCambiosEstructura = (idEstructura) => cambiosEstructuras.find((c) => c.idEstructura === idEstructura)?.cambios ?? [];

    const handleGuardarCostos = async ({ rutaId, pagoChofer, pagoChoferSencillo, precioCliente, pagoSubcontratista, observaciones: obs }) => {
        setGuardandoCostos(true);
        try {
            const nuevoEstatus = estatus === 5 ? 5 : 9;

            await actualizarCostosRuta({
                cotizacion: cotizacionId,
                rutaId,
                pagoChofer,
                pagoChoferSencillo,
                precioCliente,
                pagoSubcontratista,
                estatus: nuevoEstatus,
                observaciones: obs,
            });

            setCotizacion((prev) => ({
                ...prev,
                estatus: nuevoEstatus,
                rutas: { ...prev.rutas, pagoChofer, pagoChoferSencillo, precioCliente, pagoSubcontratista, observaciones: obs },
            }));

            setDialogCostosVisible(false);

            await Swal.fire({ icon: "success", title: "Costos guardados", text: "Los costos de la ruta se actualizaron correctamente.", timer: 2000, showConfirmButton: false });
        } catch {
            await Swal.fire({ icon: "error", title: "Error al guardar", text: "No se pudieron guardar los costos. Intenta de nuevo." });
        } finally {
            setGuardandoCostos(false);
        }
    };

    const handleSolicitarCambiosClick = async () => {
        if (!motivoCambios.trim()) return;
        setEnviandoCambios(true);
        try {
            await requestChanges(cotizacionId, { changes: motivoCambios });
            onSolicitarCambios();
            setDialogCambiosVisible(false);
            setMotivoCambios("");
            await Swal.fire({ icon: "success", title: "Cambios solicitados", timer: 2000, showConfirmButton: false });
        } catch {
            await Swal.fire({ icon: "error", title: "Error al solicitar cambios" });
        } finally {
            setEnviandoCambios(false);
        }
    };

    return (
        <div className="ruta-card">
            {/* ── Cabecera de la ruta (informativa, ya no colapsa) ── */}
            <div className="ruta-card__header">
                <div className="flex align-items-center gap-3 flex-1 flex-wrap">
                    <div className="ruta-card__icono-ruta">
                        <i className="pi pi-directions" />
                    </div>
                    <div className="flex-1">
                        <h4 className="ruta-card__nombre m-0">{ruta.nombre}</h4>
                        <div className="flex align-items-center gap-2 mt-1 flex-wrap">
                            {datosHeader && <Tag value={datosHeader?.activa ? "Activa" : "Inactiva"} severity={datosHeader?.activa ? "success" : "danger"} />}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Contenido (siempre visible) ── */}
            <div className="ruta-card__contenido">
                <SeccionCostos
                    visibilidad={visibilidad}
                    ruta={ruta}
                    rutaAnterior={rutaAnterior}
                    estatus={estatus}
                    puedeDefinirCostos={puedeDefinirCostos}
                    puedeEnviarCliente={puedeEnviarCliente}
                    userId={userId}
                    onDefinirCostos={() => setDialogCostosVisible(true)}
                    onSendToClient={onSendToClient}
                    onSolicitarCambios={() => setDialogCambiosVisible(true)}
                />

                <SeccionEquipamiento
                    visibilidad={visibilidad}
                    ruta={ruta}
                    equipamiento={equipamiento}
                    historial={historial}
                    puedeConfirmarDatos={puedeConfirmarDatos}
                    verificado={verificado}
                    onValidarEstructuras={onValidarEstructuras}
                />

                {/* <SeccionCambios esModificacion={esModificacion} historial={historial} cambiosRuta={cambiosRuta} />*/}

                {visibilidad.observaciones && observaciones && (
                    <div className="ruta-card__observaciones">
                        <i className="pi pi-comment" />
                        <div>
                            <span className="ruta-card__observaciones-label">Observaciones</span>
                            {observaciones}
                        </div>
                    </div>
                )}

                {cambiosSolicitados && (
                    <div className="ruta-card__observaciones">
                        <i className="pi pi-comment" />
                        <div>
                            <span className="ruta-card__observaciones-label">Cambios Solicitados</span>
                            {cambiosSolicitados}
                        </div>
                    </div>
                )}

                <SeccionHorarios
                    visibilidad={visibilidad}
                    ruta={ruta}
                    estructurasOrdenadas={estructurasOrdenadas}
                    estructurasSinCambios={estructurasSinCambiosOrdenadas}
                    diasSemana={diasSemana}
                    esModificacion={esModificacion}
                    getCambiosEstructura={getCambiosEstructura}
                />

                {requiereGestionTrazos ? (
                    <>
                        {visibilidad.puedeEditarTrazo && ruta.estructuras?.length > 0 && (
                            <p className="ruta-card__subtitulo-ayuda mb-3">
                                {puedeGenerarTrazos
                                    ? "Debes registrar los recorridos para poder avanzar al siguiente paso."
                                    : "Primero confirma los datos de equipamiento y horarios para poder generar los recorridos."}
                            </p>
                        )}

                        {visibilidad.trazos && (
                            <MapasCard
                                ruta={ruta}
                                categoria={categoria}
                                puedeVerificarTrazos={puedeVerificarTrazos}
                                puedeGenerarTrazos={puedeGenerarTrazos}
                                visibilidad={visibilidad}
                                totalSinCambios={esModificacion ? estructurasSinCambios.length : 0}
                                onGenerarTrazo={onGenerarTrazo}
                                handleEditarAllTrazos={handleEditarAllTrazos}
                                onEditarTrazo={onEditarTrazo}
                                onValidarTrazo={onValidarTrazo}
                            />
                        )}
                    </>
                ) : (
                    visibilidad.puedeEditarTrazo && puedeVerificarTrazos && (
                        <div className="flex justify-content-end mt-2">
                            <Button label="Mandar a costeo" icon="pi pi-send" onClick={onValidarTrazo} />
                        </div>
                    )
                )}
            </div>

            <DialogCostos
                visible={dialogCostosVisible}
                observaciones={observaciones}
                onHide={() => setDialogCostosVisible(false)}
                ruta={ruta}
                onGuardar={handleGuardarCostos}
                guardando={guardandoCostos}
                user={userId}
            />

            <Dialog
                visible={dialogCambiosVisible}
                onHide={() => { setDialogCambiosVisible(false); setMotivoCambios(""); }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-refresh" style={{ color: "#ef4444" }} />
                        <span>Solicitar Cambios</span>
                    </div>
                }
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            text
                            severity="secondary"
                            onClick={() => { setDialogCambiosVisible(false); setMotivoCambios(""); }}
                            disabled={enviandoCambios}
                        />
                        <Button
                            label="Enviar solicitud"
                            icon="pi pi-send"
                            severity="danger"
                            onClick={handleSolicitarCambiosClick}
                            loading={enviandoCambios}
                            disabled={!motivoCambios.trim()}
                        />
                    </div>
                }
                style={{ width: "480px" }}
                modal
                draggable={false}
            >
                <div className="flex flex-column gap-2 pt-2">
                    <label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-color)" }}>
                        Describe los cambios que se requieren
                    </label>
                    <InputTextarea
                        value={motivoCambios}
                        onChange={(e) => setMotivoCambios(e.target.value)}
                        rows={5}
                        autoResize
                        placeholder="Ej: Ajustar horario de entrada, cambiar punto de origen..."
                        style={{ width: "100%", fontSize: "0.85rem" }}
                        autoFocus
                    />
                    <small style={{ color: "var(--text-color-secondary)" }}>{motivoCambios.length} caracteres</small>
                </div>
            </Dialog>
        </div>
    );
};

export default TarjetaRuta;